<template>
  <div class="bg-white border border-gray-100 rounded-lg shadow-sm m-4 p-4 sm:p-5">
    <h1 class="mt-2 mb-2 text-base sm:text-lg font-semibold text-gray-900">Hotspot Products Breakdown</h1>
    <div class="flex flex-col sm:flex-row flex-wrap gap-3 mt-4">
      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Start Date</h3>
        <input type="date" placeholder="YYYY-MM-DD" v-model="from" class="text-sm focus:outline-none" />
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">End Date</h3>
        <input type="date" placeholder="YYYY-MM-DD" v-model="to" class="text-sm focus:outline-none" />
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <select v-model="plan_type" class="text-sm focus:outline-none">
          <option value="unlimited">Unlimited Data Plan</option>
          <option value="capped">Capped Data Plan</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <select v-model="currency" class="text-sm focus:outline-none">
          <option value="NGN">Naira</option>
          <option value="GHC">Ghana Cedis</option>
          <option value="XOF">XOF</option>
        </select>
      </div>

      <button
        class="flex items-center justify-center px-4 py-3 rounded-lg bg-express hover:bg-expressbutton text-white text-sm font-medium transition-colors"
        @click="hotspot_sales_break_down"
      >
        Search
      </button>
    </div>
    <div class="flex p-4 mt-5 mb-5 w-full">
      <loading v-if="loading"></loading>

      <div v-if="sales_data.length > 0" class="w-full overflow-x-auto">
        <table class="text-sm text-center w-full">
          <thead>
            <tr class="border-b border-gray-200">
              <th class="px-3 py-3 font-semibold text-expressgray">S/N</th>
              <th class="px-3 py-3 font-semibold text-expressgray">Product Name</th>
              <th class="px-3 py-3 font-semibold text-expressgray">Total Amount (Sold) in {{ currency }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              class="border-b border-gray-100 hover:bg-gray-50"
              v-for="(item, i) in sales_data"
              :key="item.product_name"
            >
              <td class="px-3 py-3">{{ i + 1 }}</td>
              <td class="px-3 py-3">{{ item.product_name }}</td>
              <td class="px-3 py-3">{{ item.amount.toLocaleString() }}</td>
            </tr>
            <tr>
              <td class="px-3 py-3"></td>
              <td class="px-3 py-3 font-medium">Total</td>
              <td class="px-3 py-3 font-medium">
                {{ total_sales_data.toLocaleString() }} {{ currency }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-sm text-expressgray">No record(s) found</div>
    </div>

    <h1 class="mt-16 sm:mt-24 mb-2 text-base sm:text-lg font-semibold text-gray-900">Hotspot Active/Expired Breakdown</h1>
    <div class="flex flex-col sm:flex-row flex-wrap gap-3 mt-4">
      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Country</h3>
        <select v-model="hotspot_country" class="text-sm focus:outline-none">
          <option value="Nigeria">Nigeria</option>
          <option value="Ghana">Ghana</option>
          <option value="CIV">Cote d'Ivoire</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Category</h3>
        <select v-model="hotspot_category" class="text-sm focus:outline-none">
          <option value="active">Active</option>
          <option value="inactive">Expired</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <select v-model="hotspot_plan_type" class="text-sm focus:outline-none">
          <option value="unlimited">Unlimited Data Plan</option>
          <option value="capped">Capped Data Plan</option>
        </select>
      </div>

      <button
        class="flex items-center justify-center px-4 py-3 rounded-lg bg-express hover:bg-expressbutton text-white text-sm font-medium transition-colors"
        @click="hotspot_expiration_break_down"
      >
        Search
      </button>
    </div>

    <div class="flex p-4 mt-5 mb-5">
      <loading v-if="hotspot_loading"></loading>
      <div v-if="hotspot_users.length > 0" class="w-full mt-6">
        <!--        Download users-->
        <div class="overflow-x-auto">
        <table class="text-sm text-center w-full">
          <thead>
            <tr class="border-b border-gray-200">
              <th class="px-3 py-3 font-semibold text-expressgray">Plan Type</th>
              <th class="px-3 py-3 font-semibold text-expressgray">Country</th>
              <th class="px-3 py-3 font-semibold text-expressgray">Category</th>
              <th class="px-3 py-3 font-semibold text-expressgray">Number of Users</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b border-gray-100">
              <td class="px-3 py-3 capitalize">{{ hotspot_data.plan_type }}</td>
              <td class="px-3 py-3 capitalize">{{ hotspot_data.country }}</td>
              <td class="px-3 py-3 capitalize">{{ hotspot_data.category }}</td>
              <td class="px-3 py-3 capitalize">{{ hotspot_data.count }}</td>
            </tr>
          </tbody>
        </table>
        </div>

        <button
          class="w-full p-3 rounded-lg bg-express hover:bg-expressbutton mt-6 text-white text-sm font-medium transition-colors"
          @click="
            downloadCSVFromJson(
              `hotspot_${hotspot_data.plan_type}_${hotspot_data.country}_${hotspot_data.category}_users.csv`,
              hotspot_users
            )
          "
        >
          Download Users Report
        </button>
      </div>
      <div v-else class="text-sm text-expressgray">No record(s) found</div>
    </div>

    <!--    Top Users-->
    <h1 class="mt-16 sm:mt-24 mb-2 text-base sm:text-lg font-semibold text-gray-900">
      Hotspot Top/Low Users Data-Consumption Breakdown
    </h1>
    <div class="flex flex-col sm:flex-row flex-wrap gap-3 mt-4">
      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Country</h3>
        <select v-model="hotspot_topusers_country" class="text-sm focus:outline-none">
          <option value="Nigeria">Nigeria</option>
          <option value="Ghana">Ghana</option>
          <option value="CIV">Cote d'Ivoire</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Order</h3>
        <select v-model="hotspot_topusers_order" class="text-sm focus:outline-none">
          <option value="desc">High - Low Data Users</option>
          <option value="asc">Low - High Data Users</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Start Date</h3>
        <input
          type="date"
          placeholder="YYYY-MM-DD"
          v-model="hotspot_topusers_from"
          class="text-sm focus:outline-none"
        />
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Start Date</h3>
        <input
          type="date"
          placeholder="YYYY-MM-DD"
          v-model="hotspot_topusers_to"
          class="text-sm focus:outline-none"
        />
      </div>

      <button
        class="flex items-center justify-center px-4 py-3 rounded-lg bg-express hover:bg-expressbutton text-white text-sm font-medium transition-colors"
        @click="hotspot_topusers_break_down"
      >
        Search
      </button>
    </div>

    <div class="flex p-4 mt-5 mb-5">
      <loading v-if="hotspot_topusers_loading"></loading>
      <div v-if="hotspot_topusers.length > 0" class="w-full mt-6">
        <!--        Download users-->
        <button
          class="w-full p-3 rounded-lg bg-express hover:bg-expressbutton mt-6 text-white text-sm font-medium transition-colors"
          @click="
            downloadCSVFromJson(
              `hotspot_${hotspot_topusers_country}_${hotspot_topusers_from}_${hotspot_topusers_to}_${hotspot_topusers_order}_top_datausage_users.csv`,
              hotspot_topusers
            )
          "
        >
          Export {{ hotspot_topusers_total_users }} Users Report
        </button>
      </div>
      <div v-else class="text-sm text-expressgray">No record(s) found</div>
    </div>

    <!--    End of Top Users-->

    <!--    Retailer Sales BreakDown -->

    <h1 class="mt-16 sm:mt-24 mb-2 text-base sm:text-lg font-semibold text-gray-900">
      Hotspot Top/Low Retailer Sales Breakdown
    </h1>
    <div class="flex flex-col sm:flex-row flex-wrap gap-3 mt-4">
      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Country</h3>
        <select v-model="hotspot_retailer_sales_country" class="text-sm focus:outline-none">
          <option value="Nigeria">Nigeria</option>
          <option value="Ghana">Ghana</option>
          <option value="CIV">Cote d'Ivoire</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Order</h3>
        <select v-model="hotspot_retailer_sales_order" class="text-sm focus:outline-none">
          <option value="desc">High-Low Selling Retailers</option>
          <option value="asc">Low-High Selling Retailers</option>
        </select>
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Start Date</h3>
        <input
          type="date"
          placeholder="YYYY-MM-DD"
          v-model="hotspot_retailer_sales_from"
          class="text-sm focus:outline-none"
        />
      </div>

      <div class="flex items-center p-3 border border-gray-200 rounded-lg">
        <h3 class="mr-3 text-sm text-expressgray whitespace-nowrap">Start Date</h3>
        <input
          type="date"
          placeholder="YYYY-MM-DD"
          v-model="hotspot_retailer_sales_to"
          class="text-sm focus:outline-none"
        />
      </div>

      <button
        class="flex items-center justify-center px-4 py-3 rounded-lg bg-express hover:bg-expressbutton text-white text-sm font-medium transition-colors"
        @click="hotspot_retailer_sales_break_down"
      >
        Search
      </button>
    </div>

    <div class="flex p-4 mt-5 mb-5">
      <loading v-if="hotspot_retailer_sales_loading"></loading>
      <div v-if="hotspot_retailer_sales_users.length > 0" class="w-full mt-6">
        <!--        Download users-->
        <button
          class="w-full p-3 rounded-lg bg-express hover:bg-expressbutton mt-6 text-white text-sm font-medium transition-colors"
          @click="
            downloadCSVFromJson(
              `hotspot_${hotspot_retailer_sales_country}_${hotspot_retailer_sales_from}_${hotspot_retailer_sales_to}_${hotspot_retailer_sales_order}_retailer_sales_breakdown.csv`,
              hotspot_retailer_sales_users
            )
          "
        >
          Export {{ hotspot_retailer_sales_country }} Retailer Sales Report ,
          From {{ hotspot_retailer_sales_from }} To
          {{ hotspot_retailer_sales_to }}
        </button>
      </div>
      <div v-else class="text-sm text-expressgray">No record(s) found</div>
    </div>

    <!--    End of Retailer Sales BreakDown -->
  </div>
</template>

<script>
import Loading from "@/components/uicomponents/loader/loading";
import axios from "axios";
import * as https from "https";
import querystring from "querystring";
export default {
  name: "RdeAnalytics",
  components: { Loading },
  created() {
    this.from = new Date().toISOString().slice(0, 10);
    this.to = new Date().toISOString().slice(0, 10);
    this.hotspot_topusers_from = new Date().toISOString().slice(0, 10);
    this.hotspot_topusers_to = new Date().toISOString().slice(0, 10);
    this.hotspot_retailer_sales_from = new Date().toISOString().slice(0, 10);
    this.hotspot_retailer_sales_to = new Date().toISOString().slice(0, 10);
  },
  data() {
    return {
      from: "",
      to: "",
      plan_type: "unlimited",
      currency: "NGN",
      loading: false,
      sales_data: [],
      total_sales_data: 0,
      hotspot_country: "Nigeria",
      hotspot_plan_type: "unlimited",
      hotspot_category: "active",
      hotspot_loading: false,
      hotspot_users: [],
      hotspot_data: [],
      hotspot_topusers_loading: false,
      hotspot_topusers: [],
      hotspot_topusers_country: "Nigeria",
      hotspot_topusers_order: "desc",
      hotspot_topusers_from: "",
      hotspot_topusers_to: "",
      hotspot_topusers_total_users: 0,
      hotspot_retailer_sales_country: "Nigeria",
      hotspot_retailer_sales_order: "desc",
      hotspot_retailer_sales_from: "",
      hotspot_retailer_sales_to: "",
      hotspot_retailer_sales_users: [],
      hotspot_retailer_sales_loading: false,
    };
  },
  methods: {
    downloadCSVFromJson: function (filename, data) {
      // convert JSON to CSV
      const replacer = (key, value) => (value === null ? "" : value); // specify how you want to handle null values here
      const header = Object.keys(data[0]);
      let csv = data.map((row) =>
        header
          .map((fieldName) => JSON.stringify(row[fieldName], replacer))
          .join(",")
      );
      csv.unshift(header.join(","));
      csv = csv.join("\r\n");

      // Create link and download
      var link = document.createElement("a");
      link.setAttribute(
        "href",
        "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csv)
      );
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    hotspot_retailer_sales_break_down: function () {
      let vm = this;
      vm.hotspot_retailer_sales_loading = true;
      vm.hotspot_retailer_sales_users = [];
      var querystring = require("querystring");
      axios
        .post(
          "https://legacy.tizeti.com/geniatechapi/index.php",
          querystring.stringify({
            action: "hotspot_total_sales_by_retailers",
            country: vm.hotspot_retailer_sales_country,
            from: vm.hotspot_retailer_sales_from,
            to: vm.hotspot_retailer_sales_to,
            order: vm.hotspot_retailer_sales_order,
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        )
        .then(function (response) {
          vm.hotspot_retailer_sales_loading = false;
          vm.hotspot_retailer_sales_users = response.data.users;
          // console.log("hotspot_expiration_break_down response",response.data);
        })
        .catch(function (error) {
          vm.hotspot_retailer_sales_loading = false;
          console.error(error);
        });
    },
    hotspot_topusers_break_down: function () {
      let vm = this;
      vm.hotspot_topusers_loading = true;
      vm.hotspot_topusers = [];
      var querystring = require("querystring");
      axios
        .post(
          "https://legacy.tizeti.com/geniatechapi/index.php",
          querystring.stringify({
            action: "hotspot_top_users",
            country: vm.hotspot_topusers_country,
            from: vm.hotspot_topusers_from,
            to: vm.hotspot_topusers_to,
            order: vm.hotspot_topusers_order,
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        )
        .then(function (response) {
          vm.hotspot_topusers_loading = false;
          vm.hotspot_topusers_total_users = response.data.total_users;
          vm.hotspot_topusers = response.data.users;
          // console.log("hotspot_expiration_break_down response",response.data);
        })
        .catch(function (error) {
          vm.hotspot_topusers_loading = false;
          console.error(error);
        });
    },
    hotspot_expiration_break_down: function () {
      let vm = this;
      vm.hotspot_loading = true;
      vm.hotspot_users = [];

      var querystring = require("querystring");
      //...
      axios
        .post(
          "https://legacy.tizeti.com/geniatechapi/index.php",
          querystring.stringify({
            action: "hotspot_expiration_tracker",
            plan_type: vm.hotspot_plan_type,
            country: vm.hotspot_country,
            category: vm.hotspot_category,
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        )
        .then(function (response) {
          vm.hotspot_loading = false;
          vm.hotspot_data = response.data;
          vm.hotspot_users = response.data.users;
          console.log("hotspot_expiration_break_down response", response.data);
        })
        .catch(function (error) {
          vm.hotspot_loading = false;
          console.error(error);
        });
    },
    hotspot_sales_break_down: function () {
      let vm = this;
      vm.total_sales_data = 0;
      vm.loading = true;
      const options = {
        method: "POST",
        url: "https://hotspot.tizetiwifi.com/api/sales_analytics",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        data: {
          plan_type: vm.plan_type,
          currency: vm.currency,
          from: vm.from,
          to: vm.to,
        },
      };

      axios
        .request(options)
        .then(function (response) {
          //console.log(response.data.data);
          vm.loading = false;
          vm.sales_data = response.data.data;
          vm.sales_data.map((item) => {
            console.log("sum of item ", item.amount);
            vm.total_sales_data =
              parseFloat(vm.total_sales_data) + parseFloat(item.amount);
          });
        })
        .catch(function (error) {
          vm.loading = false;
          alert("An error occurred, please try again later");
          console.error(error);
        });
    },
  },
};
</script>

<style scoped></style>
